import {
  App,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { academicsApi, type Band, type GradingScale } from '@/api/academics';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

interface FormShape {
  name: string;
  bands: Band[];
}

export function GradingScalesPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('ACADEMICS:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('ACADEMICS:EDIT'));

  const [editing, setEditing] = useState<GradingScale | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data, isLoading } = useQuery({
    queryKey: ['academics', 'grading-scales'],
    queryFn: () => academicsApi.listGradingScales(),
  });

  const upsert = useMutation({
    mutationFn: (v: FormShape) =>
      editing ? academicsApi.updateGradingScale(editing.id, v) : academicsApi.createGradingScale(v),
    onSuccess: () => {
      message.success(editing ? 'Scale updated' : 'Scale created · activate it to use');
      qc.invalidateQueries({ queryKey: ['academics', 'grading-scales'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const activate = useMutation({
    mutationFn: (id: number) => academicsApi.activateGradingScale(id),
    onSuccess: () => {
      message.success('Activated');
      qc.invalidateQueries({ queryKey: ['academics', 'grading-scales'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({
      bands: [
        { min_pct: 0, max_pct: 39, grade: 'F', remarks: 'Fail' },
        { min_pct: 40, max_pct: 100, grade: 'P', remarks: 'Pass' },
      ],
    });
  };
  const openEdit = (row: GradingScale) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({ name: row.name, bands: row.bands });
  };
  const open = creating || editing != null;

  const columns: TableProps<GradingScale>['columns'] = [
    {
      title: 'Scale',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.name}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.bands.length} bands
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Bands',
      render: (_, row) => (
        <Space wrap size={[4, 4]}>
          {row.bands.map((b, i) => (
            <Tag key={i} color="processing">
              {b.min_pct}–{b.max_pct}% · {b.grade}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      width: 130,
      render: (active: boolean, row) =>
        active ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Active
          </Tag>
        ) : canEdit ? (
          <Button size="small" onClick={() => activate.mutate(row.id)} loading={activate.isPending}>
            Activate
          </Button>
        ) : (
          <Tag>Inactive</Tag>
        ),
    },
    {
      title: 'Actions',
      width: 110,
      align: 'right',
      render: (_, row) =>
        canEdit ? (
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Academics"
        title="Grading scales"
        description="Bands map a percentage to a letter grade. Result calculation reads from the active scale."
        count={{ value: data?.length ?? 0, label: 'scales' }}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New scale
            </Button>
          ) : null
        }
      />

      <Table
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={data ?? []}
        pagination={false}
      />

      <Drawer
        title={editing ? `Edit · ${editing.name}` : 'New grading scale'}
        open={open}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        size={620}
        destroyOnHidden
        extra={
          <Space>
            <Button
              onClick={() => {
                setEditing(null);
                setCreating(false);
              }}
            >
              Cancel
            </Button>
            <Button type="primary" loading={upsert.isPending} onClick={() => form.submit()}>
              {editing ? 'Save' : 'Create'}
            </Button>
          </Space>
        }
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            <Form.Item label="Name" name="name" rules={[{ required: true, max: 100 }]}>
              <Input autoFocus placeholder="e.g. Standard Pakistani Grading" />
            </Form.Item>

            <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
              Bands · must cover 0–100% with no gaps or overlaps
            </Typography.Text>

            <Form.List name="bands">
              {(fields, { add, remove }) => (
                <Space orientation="vertical" style={{ width: '100%' }} size={8}>
                  {fields.map((field) => (
                    <Space key={field.key} align="baseline" size={6} wrap>
                      <Form.Item
                        {...field}
                        name={[field.name, 'min_pct']}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber min={0} max={100} placeholder="from" style={{ width: 80 }} />
                      </Form.Item>
                      <span>–</span>
                      <Form.Item
                        {...field}
                        name={[field.name, 'max_pct']}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber min={0} max={100} placeholder="to" style={{ width: 80 }} />
                      </Form.Item>
                      <span>%</span>
                      <Form.Item
                        {...field}
                        name={[field.name, 'grade']}
                        rules={[{ required: true, max: 5 }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="grade" style={{ width: 80 }} />
                      </Form.Item>
                      <Form.Item {...field} name={[field.name, 'remarks']} style={{ marginBottom: 0, flex: 1, minWidth: 160 }}>
                        <Input placeholder="remarks (optional)" />
                      </Form.Item>
                      <Button
                        size="small"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    </Space>
                  ))}
                  <Button onClick={() => add({ min_pct: 0, max_pct: 0, grade: '', remarks: '' })} block>
                    + Add band
                  </Button>
                </Space>
              )}
            </Form.List>
          </Form>
        )}
      </Drawer>
    </div>
  );
}
